// @ts-strict-ignore
import { InAppPurchase, RedeemCode } from 'mytours-read-api-typescript'
import { Collection, CouponCode } from 'mytours-read-common'
import { getCurrentLanguage, getDataFromLocalStorage, setDataToLocalStorage } from '../platform'
import { DEFAULT_APP_INFO } from '../settings/functions'

export async function saveCouponCodeToDisk(projectId: string, couponCode: CouponCode): Promise<void> {
	const key = projectId + '_coupon_codes'

	const codes = (await getDataFromLocalStorage(key, true)) || []
	const couponCodeIndex = codes.findIndex(
		item =>
			item.code === couponCode.code &&
			item.linkedId === couponCode.linkedId &&
			item.linkedType === couponCode.linkedType,
	)
	if (couponCodeIndex === -1) {
		codes.push(couponCode)
	} else {
		// Preserve the original redeem timestamp across re-saves (e.g. re-redeeming the same code).
		codes[couponCodeIndex] = { ...couponCode, addedAt: codes[couponCodeIndex].addedAt ?? couponCode.addedAt }
	}

	return setDataToLocalStorage(key, codes, true)
}

export function loadCouponCodesFromDisk(projectId: string): Promise<CouponCode[] | undefined> {
	const key = projectId + '_coupon_codes'
	return getDataFromLocalStorage(key, true)
}

export async function removeCouponCodeFromDisk(projectId: string, couponCode: CouponCode): Promise<void> {
	const key = projectId + '_coupon_codes'

	const codes = (await getDataFromLocalStorage(key, true)) || []
	// Match the full identity, like saveCouponCodeToDisk: the same code string can legitimately
	// exist for different entities (e.g. a venue reusing one coupon for a bundle and a collection),
	// and removing by code alone could delete the wrong, still-valid entry.
	const couponCodeIndex = codes.findIndex(
		item =>
			item.code === couponCode.code &&
			item.linkedId === couponCode.linkedId &&
			item.linkedType === couponCode.linkedType,
	)

	if (couponCodeIndex !== -1) {
		codes.splice(couponCodeIndex, 1)
	}

	return setDataToLocalStorage(key, codes, true)
}

// Cache the IAP catalogue per app version (key matches stqry-rn). The bundle redeem saga reads
// this to resolve a bundle's items; the bundles slice reconciles against it on each fetch.
export function saveInAppPurchasesToDisk(appId: string, version: string, data: InAppPurchase[]): Promise<void> {
	const key = appId + '_iap_' + version
	return setDataToLocalStorage(key, data, true)
}

export function loadInAppPurchasesDataFromDisk(appId: string, version: string): Promise<InAppPurchase[] | undefined> {
	const key = appId + '_iap_' + version
	return getDataFromLocalStorage(key, true)
}

const getCodes = async (linkedId: number, linkedType: RedeemCode.LinkedTypeEnum): Promise<CouponCode[]> => {
	const codes = (await loadCouponCodesFromDisk(DEFAULT_APP_INFO.projectId)) || []

	return codes.filter(code => code?.linkedId === linkedId && code?.linkedType === linkedType)
}

// The device's stored redemption of this exact code for this target, if any. Checked before the redeem call so a
// held code is not redeemed again, which wastes a redemption or fails with MaxRedemptions despite access. Boot
// revalidation still decides server-side revocation; this only skips the redeem call.
export const redeemedCodeOnDevice = async (
	code: string,
	linkedId: number,
	linkedType: RedeemCode.LinkedTypeEnum,
): Promise<CouponCode | undefined> => {
	try {
		const codes = await getCodes(linkedId, linkedType)
		return codes.find(stored => stored?.code === code)
	} catch {
		// Best-effort short-circuit only: a failed disk read (corrupt entry, storage access denied)
		// must fall through to the normal redeem rather than reject and strand the caller, so treat
		// "can't tell" as "not redeemed here".
		return undefined
	}
}

export const validateAppCode = async (
	validateCode: (couponCode: CouponCode, linkedId: number) => void,
): Promise<void> => {
	const projectId = Number(DEFAULT_APP_INFO.projectId)
	const codes = await getCodes(projectId, RedeemCode.LinkedTypeEnum.Project)

	if (codes.length > 0) {
		validateCode(codes[0], projectId)
	}
}

export const validateCollectionCode = async (
	collection: Collection,
	validateCode: (couponCode: CouponCode, linkedId: number) => void | Promise<boolean>,
): Promise<void | boolean> => {
	if (!collection) {
		return
	}

	const codes = await getCodes(collection.id, RedeemCode.LinkedTypeEnum.Collection)

	if (codes.length > 0) {
		const isValid = await validateCode(codes[0], collection.id)
		if (typeof isValid === 'boolean') {
			return isValid
		}
	} else {
		return false
	}
}

export const formatCurrency = (currency: string, amount: number) => {
	const formatter = new Intl.NumberFormat(getCurrentLanguage(), {
		style: 'currency',
		currency,
	})

	return formatter.format(amount)
}
